import { createRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { IconPlus, IconMailForward, IconRefresh, IconTrash } from '@tabler/icons-react';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { canSeeTeam } from '../../../../lib/capabilities.js';
import {
  useDeactivateUser,
  usePendingInvitations,
  useReactivateUser,
  useResendInvitation,
  useRevokeInvitation,
  useTeamUsers,
  type PendingInvitation,
  type TeamUser,
} from '../../../../hooks/useTeam.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { Button } from '../../../../components/ui/Button.js';
import { InviteModal } from '../../../../components/team/InviteModal.js';
import { formatDate } from '../../../../lib/format.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/team',
  // Gates on canSeeTeam (Brief T1): role === 'admin' AND the manage_users
  // CAPABILITY. The role condition is deliberate — the UI stays stricter than
  // the server (fail-closed), and a bare admin who used to reach this screen's
  // 403 banners is now redirected instead. super_admin is not 'admin', so they
  // stay redirected as before (their team API answers 400 TENANT_REQUIRED).
  beforeLoad: () => {
    if (!canSeeTeam(useAuthStore.getState().user)) throw redirect({ to: '/app' });
  },
  component: TeamPage,
});

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super admin',
  admin: 'Agency admin',
  consultant: 'Consultant',
};

function RolePill({ role }: { role: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-line bg-cream-deep/50 px-2.5 py-0.5 text-xs font-medium text-navy">
      {ROLE_LABELS[role] ?? role}
    </span>
  );
}

function StatusPill({ deactivated }: { deactivated: boolean }) {
  if (deactivated) {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
        style={{ backgroundColor: '#FBEAEA', color: '#A83232', border: '1px solid #F0D0D0' }}
      >
        Deactivated
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
      style={{ backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' }}
    >
      Active
    </span>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function MemberRow({ user, isSelf }: { user: TeamUser; isSelf: boolean }) {
  const [confirming, setConfirming] = useState(false);
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const deactivated = user.deactivated_at !== null;

  return (
    <tr className={`hover:bg-cream/30 transition-colors ${deactivated ? 'opacity-60' : ''}`}>
      <td className="px-5 py-3.5 font-medium text-ink">{user.full_name}</td>
      <td className="px-5 py-3.5 text-ink-soft">{user.email}</td>
      <td className="px-5 py-3.5">
        <RolePill role={user.role} />
      </td>
      <td className="px-5 py-3.5 text-muted text-xs">
        {user.last_active_at ? timeAgo(user.last_active_at) : 'Never'}
      </td>
      <td className="px-5 py-3.5">
        <StatusPill deactivated={deactivated} />
      </td>
      <td className="px-5 py-3.5 text-right">
        {isSelf ? (
          <span className="text-muted-soft text-xs">You</span>
        ) : deactivated ? (
          <Button
            variant="secondary"
            size="sm"
            loading={reactivate.isPending}
            onClick={() => reactivate.mutate(user.id)}
          >
            Reactivate
          </Button>
        ) : confirming ? (
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-muted">Deactivate?</span>
            <Button
              variant="danger"
              size="sm"
              loading={deactivate.isPending}
              onClick={() =>
                deactivate.mutate(user.id, { onSuccess: () => setConfirming(false) })
              }
            >
              Confirm
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={deactivate.isPending}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
            Deactivate
          </Button>
        )}
      </td>
    </tr>
  );
}

function InviteRow({ inv }: { inv: PendingInvitation }) {
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();

  return (
    <tr className="hover:bg-cream/30 transition-colors">
      <td className="px-5 py-3.5 text-ink">{inv.email}</td>
      <td className="px-5 py-3.5">
        <RolePill role={inv.role} />
      </td>
      <td className="px-5 py-3.5 text-muted text-xs">{timeAgo(inv.created_at)}</td>
      <td className="px-5 py-3.5 text-muted text-xs">Expires {formatDate(inv.expires_at)}</td>
      <td className="px-5 py-3.5 text-right">
        {confirmingRevoke ? (
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-muted">Revoke invite?</span>
            <Button
              variant="danger"
              size="sm"
              loading={revoke.isPending}
              onClick={() =>
                revoke.mutate(inv.id, { onSuccess: () => setConfirmingRevoke(false) })
              }
            >
              Confirm
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => setConfirmingRevoke(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<IconRefresh size={14} />}
              loading={resend.isPending}
              onClick={() => resend.mutate(inv.id)}
            >
              Resend
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<IconTrash size={14} />}
              onClick={() => setConfirmingRevoke(true)}
            >
              Revoke
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

function TeamPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const users = useTeamUsers();
  const invitations = usePendingInvitations();

  function handleInviteSuccess(email: string) {
    setModalOpen(false);
    setSentTo(email);
  }

  return (
    <div>
      <AdminPageHeader
        title="Team"
        description="Invite consultants and admins to your agency workspace."
        actions={
          <Button leftIcon={<IconPlus size={16} />} onClick={() => setModalOpen(true)}>
            Invite
          </Button>
        }
      />

      {sentTo && (
        <div
          className="mb-6 rounded-lg px-4 py-3 text-sm"
          style={{ backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' }}
        >
          <strong>Invite sent</strong> to{' '}
          <span style={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace' }}>{sentTo}</span>.
        </div>
      )}

      {/* Team members */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-muted mb-3">
          Team members
        </h2>

        {users.isLoading && <p className="text-muted">Loading…</p>}
        {users.error && (
          <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
            Couldn't load team members: {(users.error as Error).message}
          </p>
        )}

        {users.data && users.data.users.length > 0 && (
          <div className="bg-white border border-line rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-cream/60 border-b border-line">
                <tr className="text-left text-muted text-xs uppercase tracking-wider">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Role</th>
                  <th className="px-5 py-3 font-medium">Last active</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {users.data.users.map((u) => (
                  <MemberRow key={u.id} user={u} isSelf={u.id === currentUserId} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Pending invitations */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-muted mb-3">
          Pending invitations
        </h2>

        {invitations.isLoading && <p className="text-muted">Loading…</p>}
        {invitations.error && (
          <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
            Couldn't load invitations: {(invitations.error as Error).message}
          </p>
        )}

        {invitations.data && invitations.data.invitations.length === 0 && (
          <div className="border border-dashed border-line-strong rounded-xl p-10 text-center bg-white/40">
            <IconMailForward size={28} className="text-muted-soft mx-auto mb-3" />
            <p className="text-ink-soft font-medium">No pending invitations.</p>
          </div>
        )}

        {invitations.data && invitations.data.invitations.length > 0 && (
          <div className="bg-white border border-line rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-cream/60 border-b border-line">
                <tr className="text-left text-muted text-xs uppercase tracking-wider">
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Role</th>
                  <th className="px-5 py-3 font-medium">Sent</th>
                  <th className="px-5 py-3 font-medium">Expires</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {invitations.data.invitations.map((inv) => (
                  <InviteRow key={inv.id} inv={inv} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <InviteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />
    </div>
  );
}
